<template>
  <div class="pw-map">
    <div class="pw-map__canvas" ref="map"></div>

    <div class="pw-map__legend">
      <div><span class="pw-map__dot" :style="{ background: colors.active }"></span>Active franchise</div>
      <div><span class="pw-map__dot" :style="{ background: colors.inactive }"></span>Inactive franchise</div>
    </div>

    <div v-if="unplaced.length" class="pw-map__missing">
      <v-icon size="15" color="#8d99ad">mdi-map-marker-off-outline</v-icon>
      No location set for
      <span v-for="(f, i) in unplaced" :key="f.id">
        <router-link :to="`/franchisees/${encryptIt(f.id)}`">{{ f.name }}</router-link
        >{{ i < unplaced.length - 1 ? ", " : "" }}
      </span>
    </div>
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { formatCurrency } from "./format";

// Status colours (good / critical); each marker's popup also names its status
const COLORS = { active: "#0ca30c", inactive: "#d03b3b" };

export default {
  name: "FranchiseMap",
  props: {
    // Rows from the dashboard API: id, name, zone, is_active, latitude, longitude, revenue
    franchises: { type: Array, required: true },
  },
  data() {
    return { colors: COLORS };
  },
  computed: {
    placed() {
      return this.franchises.filter((f) => f.latitude != null && f.longitude != null);
    },
    unplaced() {
      return this.franchises.filter((f) => f.latitude == null || f.longitude == null);
    },
  },
  watch: {
    franchises() {
      this.drawMarkers();
    },
  },
  mounted() {
    this.map = L.map(this.$refs.map, { scrollWheelZoom: false, attributionControl: true });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 18,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(this.map);
    this.layer = L.layerGroup().addTo(this.map);
    this.drawMarkers();

    // The card can change size with the layout; keep the tiles filling it
    this.observer = new ResizeObserver(() => this.map && this.map.invalidateSize());
    this.observer.observe(this.$refs.map);
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    if (this.map) this.map.remove();
  },
  methods: {
    drawMarkers() {
      if (!this.map) return;
      this.layer.clearLayers();

      this.placed.forEach((f) => {
        const status = f.is_active ? "Active" : "Inactive";
        L.circleMarker([f.latitude, f.longitude], {
          radius: 9,
          weight: 2,
          color: "#ffffff",
          fillColor: f.is_active ? COLORS.active : COLORS.inactive,
          fillOpacity: 1,
        })
          .bindTooltip(f.name, { direction: "top", offset: [0, -8] })
          .bindPopup(
            `<strong>${this.escape(f.name)}</strong><br>` +
              `${this.escape(f.zone || "No zone")} &middot; ${status}<br>` +
              `Revenue: ${formatCurrency(f.revenue)}<br>` +
              `Walks: ${f.total} &middot; Customers: ${f.customers}`
          )
          .addTo(this.layer);
      });

      if (this.placed.length) {
        const bounds = L.latLngBounds(this.placed.map((f) => [f.latitude, f.longitude]));
        this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
      } else {
        // Mumbai region, where the network starts
        this.map.setView([19.07, 72.95], 9);
      }
    },
    escape(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
  },
};
</script>

<style scoped>
.pw-map {
  position: relative;
}

.pw-map__canvas {
  height: 360px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e4e8ee;
  z-index: 0;
}

.pw-map__legend {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 500;
  background: #ffffff;
  border: 1px solid rgba(11, 11, 11, 0.1);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 0.75rem;
  color: #1a2436;
  line-height: 1.8;
  box-shadow: 0 2px 8px rgba(16, 24, 40, 0.08);
}

.pw-map__dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: -1px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(11, 11, 11, 0.15);
}

.pw-map__missing {
  margin-top: 10px;
  font-size: 0.75rem;
  color: #66738a;
}
</style>
